// Smart Daara - Course Editor Page
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { courseAPI, quizAPI, userAPI } from '../../lib/api';
import api from '../../lib/api';
import { buildTenantPath } from '../../lib/routing';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  ArrowLeft, Save, Plus, Pencil, Trash2, GripVertical,
  Play, FileText, Youtube, Link, Eye, EyeOff, FileQuestion, Upload, Video, UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import VideoUploader from '../../components/shared/VideoUploader';
import PDFUploader from '../../components/shared/PDFUploader';

export default function CourseEditor() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isNew = !courseId || courseId === 'new';
  const slug = user?.tenant_slug;
  const isTeacher = user?.role === 'teacher';
  const p = (sub) => buildTenantPath(slug, sub);
  const base = isTeacher ? 'teacher' : 'school';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  // Currently-logged teacher record id (if the user is a teacher)
  const [myTeacherId, setMyTeacherId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  
  // Course data
  const [course, setCourse] = useState({
    title: '',
    title_fr: '',
    description_en: '',
    description_fr: '',
    subject_id: '',
    teacher_id: '',
    order: 0,
    is_published: true,
    tenant_id: user?.tenant_id || ''
  });

  // Lessons
  const [lessons, setLessons] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [lessonModalOpen, setLessonModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    title: '',
    title_fr: '',
    description_en: '',
    description_fr: '',
    lesson_type: 'video',
    video_source: 'youtube', // 'youtube' or 'bunny'
    youtube_url: '',
    bunny_video_url: '',
    pdf_url: '',
    pdf_pages: null,
    teacher_id: '',
    video_duration_seconds: 600,
    order: 1,
    is_published: true
  });

  useEffect(() => {
    loadData();
  }, [courseId]);

  // Auto-set course.teacher_id to the current user when they are a teacher
  // (only for new courses or when the field is empty).
  useEffect(() => {
    if (isTeacher && myTeacherId && !course.teacher_id) {
      setCourse((prev) => ({ ...prev, teacher_id: myTeacherId }));
    }
  }, [isTeacher, myTeacherId, course.teacher_id]);

  const loadData = async () => {
    try {
      // Load subjects, classes and teachers for dropdown
      const [subjectsRes, classesRes, teachersRes] = await Promise.all([
        courseAPI.listSubjects({ tenant_id: user?.tenant_id }),
        courseAPI.listClasses({ tenant_id: user?.tenant_id }),
        userAPI.listTeachers()
      ]);
      setSubjects(subjectsRes.data || []);
      setClasses(classesRes.data || []);
      const teachersList = teachersRes.data || [];
      setTeachers(teachersList);
      // Resolve current user's teacher record id when relevant
      if (isTeacher) {
        const mine = teachersList.find((t) => t.user_id === user.id);
        if (mine) setMyTeacherId(mine.id);
      }

      if (!isNew) {
        // Load course
        const courseRes = await courseAPI.getCourse(courseId);
        const c = courseRes.data || {};
        // Backward compat: prefer description_en, fall back to legacy description
        setCourse({ ...c, description_en: c.description_en || c.description || '' });

        // Load lessons
        const lessonsRes = await courseAPI.listLessons(courseId);
        setLessons(lessonsRes.data || []);

        // Load quizzes
        try {
          const quizzesRes = await quizAPI.listQuizzes(courseId);
          setQuizzes(quizzesRes.data || []);
        } catch (e) {
          console.error('Error loading quizzes:', e);
          setQuizzes([]);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (field, value) => {
    setCourse(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCourse = async () => {
    if (!course.title_fr) {
      toast.error(language === 'fr' ? 'Le titre est requis' : 'Title is required');
      return;
    }
    // Determine effective teacher_id (teacher role auto-assigns to themselves)
    let effectiveTeacherId = course.teacher_id;
    if (!effectiveTeacherId && isTeacher && myTeacherId) {
      effectiveTeacherId = myTeacherId;
    }
    if (!effectiveTeacherId) {
      toast.error(language === 'fr'
        ? 'Veuillez affecter un enseignant à ce cours'
        : 'Please assign a teacher to this course');
      return;
    }

    setSaving(true);
    try {
      let savedCourse;
      if (isNew) {
        const res = await courseAPI.createCourse({
          ...course,
          teacher_id: effectiveTeacherId,
          tenant_id: user?.tenant_id
        });
        savedCourse = res.data;
        toast.success(language === 'fr' ? 'Cours créé!' : 'Course created!');
        navigate(p(`${base}/courses/${savedCourse.id}/edit`), { replace: true });
      } else {
        await courseAPI.updateCourse(courseId, { ...course, teacher_id: effectiveTeacherId });
        toast.success(language === 'fr' ? 'Cours mis à jour!' : 'Course updated!');
      }
    } catch (error) {
      console.error('Error saving course:', error);
      const msg = error.response?.data?.detail
        || (language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const nextLessonOrder = () => {
    const orders = [...lessons, ...quizzes].map(i => i.order || 0);
    return (orders.length ? Math.max(...orders) : 0) + 1;
  };

  const openLessonModal = (lesson = null) => {
    // Default teacher_id: lesson's own > course's > logged-in teacher's own record > ''
    const defaultTeacherId = (lesson && lesson.teacher_id)
      || course.teacher_id
      || (isTeacher ? myTeacherId : '')
      || '';
    if (lesson) {
      setEditingLesson(lesson);
      setLessonForm({
        title: lesson.title || '',
        title_fr: lesson.title_fr || '',
        description_en: lesson.description_en || lesson.description || '',
        description_fr: lesson.description_fr || '',
        lesson_type: lesson.lesson_type || 'video',
        video_source: lesson.video_source || (lesson.bunny_video_url ? 'bunny' : 'youtube'),
        youtube_url: lesson.youtube_url || '',
        bunny_video_url: lesson.bunny_video_url || '',
        video_storage_path: lesson.video_storage_path || '',
        pdf_url: lesson.pdf_url || '',
        pdf_pages: lesson.pdf_pages || null,
        teacher_id: defaultTeacherId,
        video_duration_seconds: lesson.video_duration_seconds || 600,
        order: lesson.order || nextLessonOrder(),
        is_published: lesson.is_published !== false
      });
    } else {
      setEditingLesson(null);
      setLessonForm({
        title: '',
        title_fr: '',
        description_en: '',
        description_fr: '',
        lesson_type: 'video',
        video_source: 'youtube',
        youtube_url: '',
        bunny_video_url: '',
        video_storage_path: '',
        pdf_url: '',
        pdf_pages: null,
        teacher_id: defaultTeacherId,
        video_duration_seconds: 600,
        order: nextLessonOrder(),
        is_published: true
      });
    }
    setLessonModalOpen(true);
  };

  const handleLessonFormChange = (field, value) => {
    setLessonForm(prev => ({ ...prev, [field]: value }));
  };

  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    return match ? match[1] : null;
  };

  const handleSaveLesson = async () => {
    if (!lessonForm.title_fr) {
      toast.error(language === 'fr' ? 'Le titre est requis' : 'Title is required');
      return;
    }
    if (!lessonForm.teacher_id) {
      toast.error(language === 'fr'
        ? 'Veuillez affecter un enseignant à cette leçon'
        : 'Please assign a teacher to this lesson');
      return;
    }

    // Validate video URL based on source
    let effectiveBunnyUrl = lessonForm.bunny_video_url;
    if (lessonForm.lesson_type === 'video') {
      if (lessonForm.video_source === 'youtube' && !lessonForm.youtube_url) {
        toast.error(language === 'fr' ? 'L\'URL YouTube est requise' : 'YouTube URL is required');
        return;
      }
      if (lessonForm.video_source === 'bunny' && !effectiveBunnyUrl) {
        // Filet de sécurité : le serveur est la source de vérité — chaque upload est
        // enregistré en base. On récupère la dernière vidéo téléversée par cet utilisateur.
        try {
          const res = await api.get('/videos/list');
          const recent = (res.data || []).find(v =>
            v.cdn_url && !v.lesson_id && v.uploaded_by === user?.id &&
            (Date.now() - new Date(v.created_at).getTime()) < 30 * 60 * 1000
          );
          if (recent) {
            effectiveBunnyUrl = recent.cdn_url;
            setLessonForm(prev => ({ ...prev, bunny_video_url: recent.cdn_url }));
          }
        } catch (e) { /* le serveur tranchera */ }
        if (!effectiveBunnyUrl) {
          toast.error(language === 'fr' ? 'Veuillez téléverser une vidéo' : 'Please upload a video');
          return;
        }
      }
    }

    // Validate PDF lesson has a document
    if (lessonForm.lesson_type === 'pdf' && !lessonForm.pdf_url) {
      toast.error(language === 'fr' ? 'Veuillez téléverser un document PDF' : 'Please upload a PDF document');
      return;
    }

    // Validate YouTube URL
    if (lessonForm.lesson_type === 'video' && lessonForm.video_source === 'youtube' && lessonForm.youtube_url) {
      const videoId = extractYouTubeId(lessonForm.youtube_url);
      if (!videoId) {
        toast.error(language === 'fr' ? 'URL YouTube invalide' : 'Invalid YouTube URL');
        return;
      }
    }

    setSaving(true);
    try {
      const lessonData = {
        ...lessonForm,
        bunny_video_url: effectiveBunnyUrl,
        course_id: courseId,
        tenant_id: user?.tenant_id
      };

      if (editingLesson) {
        await courseAPI.updateLesson(editingLesson.id, lessonData);
        setLessons(prev => prev.map(l => 
          l.id === editingLesson.id ? { ...l, ...lessonData } : l
        ));
        toast.success(language === 'fr' ? 'Leçon mise à jour!' : 'Lesson updated!');
      } else {
        const res = await courseAPI.createLesson(lessonData);
        setLessons(prev => [...prev, res.data]);
        toast.success(language === 'fr' ? 'Leçon créée!' : 'Lesson created!');
      }
      setLessonModalOpen(false);
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleVideoUploadSuccess = (uploadData) => {
    const cdnUrl = uploadData?.cdn_url || uploadData?.result?.cdn_url;
    if (!cdnUrl) {
      toast.error(language === 'fr'
        ? 'Le stockage n\'a pas confirmé la vidéo — réessayez le téléversement'
        : 'Storage did not confirm the video — please retry the upload');
      return;
    }
    setLessonForm(prev => ({
      ...prev,
      bunny_video_url: cdnUrl,
      video_storage_path: uploadData?.storage_path || uploadData?.result?.storage_path || prev.video_storage_path,
      video_id: uploadData?.video_id || uploadData?.result?.video_id || prev.video_id,
      video_duration_seconds: uploadData?.duration_seconds || uploadData?.result?.duration_seconds || prev.video_duration_seconds,
      video_source: 'bunny'
    }));
    toast.success(language === 'fr' ? 'Vidéo téléversée!' : 'Video uploaded!');
  };

  const handleDeleteLesson = async (lesson) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette leçon?' 
      : 'Are you sure you want to delete this lesson?')) {
      return;
    }

    try {
      await courseAPI.deleteLesson(lesson.id);
      // Auto-renumber remaining active lessons
      try { await courseAPI.renumberLessons(courseId); } catch (e) { /* ignore */ }
      // Refresh from server to get updated orders
      const lessonsRes = await courseAPI.listLessons(courseId);
      setLessons(lessonsRes.data || []);
      toast.success(language === 'fr' ? 'Leçon supprimée & renumérotée !' : 'Lesson deleted & renumbered!');
    } catch (error) {
      console.error('Error deleting lesson:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  const handleRenumberLessons = async () => {
    try {
      const res = await courseAPI.renumberLessons(courseId);
      const lessonsRes = await courseAPI.listLessons(courseId);
      setLessons(lessonsRes.data || []);
      toast.success(res.data?.message_fr || res.data?.message 
        || (language === 'fr' ? 'Renumérotation terminée' : 'Renumbered'));
    } catch (e) {
      toast.error(language === 'fr' ? 'Erreur de renumérotation' : 'Renumber error');
    }
  };

  const handleDeleteQuiz = async (quiz) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer ce devoir?' 
      : 'Are you sure you want to delete this quiz?')) {
      return;
    }

    try {
      await quizAPI.deleteQuiz(quiz.id);
      setQuizzes(prev => prev.filter(q => q.id !== quiz.id));
      toast.success(language === 'fr' ? 'Devoir supprimé!' : 'Quiz deleted!');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  // Merged & ordered list of lessons + quizzes (for unified display)
  const combinedItems = useMemo(() => (
    [
      ...lessons.map(l => ({ ...l, _itemType: 'lesson' })),
      ...quizzes.map(q => ({ ...q, _itemType: 'quiz' }))
    ].sort((a, b) => (a.order || 0) - (b.order || 0))
  ), [lessons, quizzes]);

  // Drag & drop handlers (HTML5 native) — only lessons participate in reorder.
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) { setDragIndex(null); return; }
    // Reorder only across lesson items — quizzes stay pinned relative to lessons.
    const items = [...combinedItems];
    const [moved] = items.splice(dragIndex, 1);
    items.splice(targetIndex, 0, moved);
    // Build new lesson_ids from the reordered lessons (skip quizzes)
    const newLessonIds = items.filter(i => i._itemType === 'lesson').map(i => i.id);
    // Optimistic UI: assign new orders based on new position
    const reorderedLessons = newLessonIds.map((id, i) => {
      const original = lessons.find(l => l.id === id);
      return { ...original, order: i + 1 };
    });
    setLessons(reorderedLessons);
    setDragIndex(null);
    try {
      await courseAPI.reorderLessons(courseId, newLessonIds);
      toast.success(language === 'fr' ? 'Ordre des leçons mis à jour' : 'Lesson order updated');
    } catch (e) {
      toast.error(language === 'fr' ? 'Erreur de réorganisation' : 'Reorder error');
      // Fallback: reload from server
      try {
        const res = await courseAPI.listLessons(courseId);
        setLessons(res.data || []);
      } catch { /* ignore */ }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="course-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(p(`${base}/courses`))}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900">
              {isNew 
                ? (language === 'fr' ? 'Nouveau Cours' : 'New Course')
                : (language === 'fr' ? 'Modifier le Cours' : 'Edit Course')
              }
            </h1>
            <p className="text-slate-500">
              {language === 'fr' 
                ? 'Gérez les informations et les leçons du cours'
                : 'Manage course information and lessons'
              }
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSaveCourse}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800"
          data-testid="save-course-btn"
        >
          {saving ? (
            <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {language === 'fr' ? 'Enregistrer' : 'Save'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Course Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit">
                {language === 'fr' ? 'Informations du Cours' : 'Course Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title_fr">
                    {language === 'fr' ? 'Titre (Français)' : 'Title (French)'} *
                  </Label>
                  <Input
                    id="title_fr"
                    value={course.title_fr}
                    onChange={(e) => handleCourseChange('title_fr', e.target.value)}
                    placeholder="Ex: Introduction au Coran"
                    data-testid="course-title-fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">
                    {language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}
                  </Label>
                  <Input
                    id="title"
                    value={course.title}
                    onChange={(e) => handleCourseChange('title', e.target.value)}
                    placeholder="Ex: Introduction to Quran"
                    data-testid="course-title-en"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description_fr">
                  {language === 'fr' ? 'Description (Français)' : 'Description (French)'}
                </Label>
                <Textarea
                  id="description_fr"
                  value={course.description_fr}
                  onChange={(e) => handleCourseChange('description_fr', e.target.value)}
                  rows={3}
                  placeholder={language === 'fr' ? 'Décrivez le contenu du cours...' : 'Describe the course content...'}
                  data-testid="course-desc-fr"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description_en">
                  {language === 'fr' ? 'Description (Anglais)' : 'Description (English)'}
                </Label>
                <Textarea
                  id="description_en"
                  value={course.description_en || ''}
                  onChange={(e) => handleCourseChange('description_en', e.target.value)}
                  rows={3}
                  data-testid="course-desc-en"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Matière' : 'Subject'}</Label>
                  <Select 
                    value={course.subject_id} 
                    onValueChange={(value) => handleCourseChange('subject_id', value)}
                  >
                    <SelectTrigger data-testid="course-subject">
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner une matière' : 'Select a subject'} />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map(subject => {
                        // Find the class for this subject
                        const classItem = classes.find(c => c.id === subject.class_id);
                        const className = classItem ? (classItem.name_fr || classItem.name) : '';
                        const subjectName = language === 'fr' ? subject.name_fr : subject.name;
                        const displayName = className ? `${subjectName} - ${className}` : subjectName;
                        
                        return (
                          <SelectItem key={subject.id} value={subject.id}>
                            {displayName}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <UserCog className="w-4 h-4 text-amber-600" />
                    {language === 'fr' ? 'Enseignant' : 'Teacher'}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={course.teacher_id || ''}
                    onValueChange={(v) => handleCourseChange('teacher_id', v)}
                    disabled={isTeacher && myTeacherId ? true : false}
                  >
                    <SelectTrigger data-testid="course-teacher">
                      <SelectValue placeholder={language === 'fr' ? 'Sélectionner un enseignant' : 'Select a teacher'} />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map((tt) => (
                        <SelectItem key={tt.id} value={tt.id}>
                          {tt.first_name} {tt.last_name}{tt.specialization ? ` — ${tt.specialization}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isTeacher && myTeacherId && (
                    <p className="text-xs text-slate-500">
                      {language === 'fr' ? 'Auto-affecté à vous' : 'Auto-assigned to you'}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Statut' : 'Status'}</Label>
                  <div className="flex items-center gap-3 h-10">
                    <Switch
                      checked={course.is_published}
                      onCheckedChange={(checked) => handleCourseChange('is_published', checked)}
                      data-testid="course-published"
                    />
                    <span className={`text-sm font-medium ${course.is_published ? 'text-emerald-700' : 'text-slate-500'}`}>
                      {course.is_published 
                        ? (language === 'fr' ? 'Publié' : 'Published')
                        : (language === 'fr' ? 'Brouillon' : 'Draft')
                      }
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-order">
                  {language === 'fr' ? 'Rang dans la classe' : 'Rank in class'}
                </Label>
                <Input
                  id="course-order"
                  type="number"
                  min={0}
                  value={course.order ?? 0}
                  onChange={(e) => handleCourseChange('order', parseInt(e.target.value, 10) || 0)}
                  className="max-w-[200px]"
                  data-testid="course-order"
                />
                <p className="text-xs text-slate-500">
                  {language === 'fr' 
                    ? 'Position d\'affichage dans la liste des cours (1 = premier). Laisser 0 pour ordre par défaut.'
                    : 'Display position in course list (1 = first). Leave 0 for default order.'
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Course Content Section: Lessons + Quizzes merged */}
          {!isNew && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">
                    {language === 'fr' ? 'Contenu du cours' : 'Course Content'}
                  </CardTitle>
                  <CardDescription>
                    {lessons.length} {language === 'fr' ? 'leçon(s)' : 'lesson(s)'}
                    {' · '}
                    {quizzes.length} {language === 'fr' ? 'devoir(s)' : 'quiz(zes)'}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRenumberLessons}
                    title={language === 'fr' ? 'Renuméroter les leçons' : 'Renumber lessons'}
                    data-testid="renumber-lessons-btn"
                  >
                    <GripVertical className="w-4 h-4 mr-1" />
                    {language === 'fr' ? 'Renuméroter' : 'Renumber'}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate(p(`${base}/courses/${courseId}/quiz/new`))}
                    data-testid="add-quiz-btn"
                  >
                    <FileQuestion className="w-4 h-4 mr-2 text-amber-600" />
                    {language === 'fr' ? 'Devoir' : 'Quiz'}
                  </Button>
                  <Button onClick={() => openLessonModal()} data-testid="add-lesson-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Leçon' : 'Lesson'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {combinedItems.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucun contenu pour le moment' : 'No content yet'}</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3"
                      onClick={() => openLessonModal()}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {language === 'fr' ? 'Créer la première leçon' : 'Create first lesson'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {combinedItems.map((item, index) => {
                      const isQuiz = item._itemType === 'quiz';
                      const isLesson = !isQuiz;
                      return (
                        <div 
                          key={`${item._itemType}-${item.id}`}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            isQuiz 
                              ? 'bg-amber-50 hover:bg-amber-100 border border-amber-200' 
                              : 'bg-slate-50 hover:bg-slate-100'
                          } ${dragIndex === index ? 'opacity-40' : ''}`}
                          data-testid={`content-item-${index}`}
                          draggable={isLesson}
                          onDragStart={() => isLesson && handleDragStart(index)}
                          onDragOver={isLesson ? handleDragOver : undefined}
                          onDrop={() => isLesson && handleDrop(index)}
                        >
                          <GripVertical
                            className={`w-4 h-4 ${isLesson ? 'text-slate-400 cursor-move' : 'text-slate-300'}`}
                            title={isLesson ? (language === 'fr' ? 'Glisser pour réorganiser' : 'Drag to reorder') : ''}
                          />
                          
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            isQuiz
                              ? 'bg-amber-100 text-amber-700'
                              : item.lesson_type === 'video' 
                                ? (item.bunny_video_url ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600')
                                : 'bg-blue-100 text-blue-600'
                          }`}>
                            {isQuiz ? (
                              <FileQuestion className="w-5 h-5" />
                            ) : item.lesson_type === 'video' ? (
                              item.bunny_video_url ? <Video className="w-5 h-5" /> : <Youtube className="w-5 h-5" />
                            ) : (
                              <FileText className="w-5 h-5" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-900 truncate">
                                {index + 1}. {item.title_fr || item.title}
                              </p>
                              {isQuiz && (
                                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                                  {language === 'fr' ? 'Devoir' : 'Quiz'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500 truncate">
                              {isQuiz ? (
                                <span className="flex items-center gap-1">
                                  <FileQuestion className="w-3 h-3" />
                                  {(item.questions?.length || 0)} {language === 'fr' ? 'question(s)' : 'question(s)'}
                                  {' · '}
                                  {language === 'fr' ? 'Réussite' : 'Pass'}: {item.passing_score || 80}%
                                </span>
                              ) : (
                                <>
                                  {item.lesson_type === 'video' && item.bunny_video_url && (
                                    <span className="flex items-center gap-1">
                                      <Upload className="w-3 h-3" />
                                      {language === 'fr' ? 'Vidéo téléversée' : 'Uploaded video'}
                                    </span>
                                  )}
                                  {item.lesson_type === 'video' && item.youtube_url && !item.bunny_video_url && (
                                    <span className="flex items-center gap-1">
                                      <Link className="w-3 h-3" />
                                      {item.youtube_url.substring(0, 50)}...
                                    </span>
                                  )}
                                  {item.lesson_type === 'pdf' && item.pdf_url && (
                                    <span className="flex items-center gap-1">
                                      <Link className="w-3 h-3" />
                                      PDF Document
                                    </span>
                                  )}
                                </>
                              )}
                            </p>
                          </div>

                          <div className="flex items-center gap-1">
                            {item.is_published !== false ? (
                              <Eye className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <EyeOff className="w-4 h-4 text-slate-400" />
                            )}
                          </div>

                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => isQuiz 
                                ? navigate(p(`${base}/courses/${courseId}/quiz/${item.id}`))
                                : openLessonModal(item)
                              }
                              data-testid={isQuiz ? `edit-quiz-${index}` : `edit-lesson-${index}`}
                            >
                              <Pencil className="w-4 h-4 text-slate-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => isQuiz ? handleDeleteQuiz(item) : handleDeleteLesson(item)}
                              data-testid={isQuiz ? `delete-quiz-${index}` : `delete-lesson-${index}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-4 pl-1">
                  {language === 'fr' 
                    ? '💡 Les devoirs bloquent l\'accès aux leçons suivantes tant qu\'ils ne sont pas réussis (≥ 80% par défaut).'
                    : '💡 Quizzes block access to following lessons until passed (≥ 80% by default).'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit text-base">
                {language === 'fr' ? 'Aperçu' : 'Preview'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                {lessons.length > 0 && lessons[0].youtube_url ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${extractYouTubeId(lessons[0].youtube_url)}`}
                    title="Preview"
                    className="w-full h-full rounded-lg"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                ) : (
                  <Play className="w-12 h-12 text-slate-300" />
                )}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{language === 'fr' ? 'Leçons' : 'Lessons'}</span>
                  <span className="font-medium">{lessons.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{language === 'fr' ? 'Vidéos' : 'Videos'}</span>
                  <span className="font-medium">{lessons.filter(l => l.lesson_type === 'video').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">PDFs</span>
                  <span className="font-medium">{lessons.filter(l => l.lesson_type === 'pdf').length}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isNew && (
            <Card className="border-0 shadow-sm bg-amber-50">
              <CardContent className="pt-6">
                <p className="text-sm text-amber-800">
                  <strong>{language === 'fr' ? 'Conseil:' : 'Tip:'}</strong>{' '}
                  {language === 'fr' 
                    ? 'Les étudiants doivent compléter chaque leçon dans l\'ordre pour débloquer la suivante.'
                    : 'Students must complete each lesson in order to unlock the next one.'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Lesson Modal */}
      <Dialog open={lessonModalOpen} onOpenChange={setLessonModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingLesson 
                ? (language === 'fr' ? 'Modifier la Leçon' : 'Edit Lesson')
                : (language === 'fr' ? 'Nouvelle Leçon' : 'New Lesson')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Français)' : 'Title (French)'} *</Label>
                <Input
                  value={lessonForm.title_fr}
                  onChange={(e) => handleLessonFormChange('title_fr', e.target.value)}
                  placeholder="Ex: Introduction"
                  data-testid="lesson-title-fr"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}</Label>
                <Input
                  value={lessonForm.title}
                  onChange={(e) => handleLessonFormChange('title', e.target.value)}
                  data-testid="lesson-title-en"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description (Français)' : 'Description (French)'}</Label>
              <Textarea
                value={lessonForm.description_fr}
                onChange={(e) => handleLessonFormChange('description_fr', e.target.value)}
                rows={2}
                data-testid="lesson-desc-fr"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description (Anglais)' : 'Description (English)'}</Label>
              <Textarea
                value={lessonForm.description_en || ''}
                onChange={(e) => handleLessonFormChange('description_en', e.target.value)}
                rows={2}
                data-testid="lesson-desc-en"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Type de Leçon' : 'Lesson Type'}</Label>
                <Select 
                  value={lessonForm.lesson_type} 
                  onValueChange={(value) => handleLessonFormChange('lesson_type', value)}
                >
                  <SelectTrigger data-testid="lesson-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">
                      <span className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-emerald-600" />
                        {language === 'fr' ? 'Vidéo' : 'Video'}
                      </span>
                    </SelectItem>
                    <SelectItem value="pdf">
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-500" />
                        PDF
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Ordre' : 'Order'}</Label>
                <Input
                  type="number"
                  min={1}
                  value={lessonForm.order}
                  onChange={(e) => handleLessonFormChange('order', parseInt(e.target.value) || 1)}
                  data-testid="lesson-order"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <UserCog className="w-4 h-4 text-amber-600" />
                {language === 'fr' ? 'Enseignant de la leçon' : 'Lesson teacher'}
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={lessonForm.teacher_id || ''}
                onValueChange={(v) => handleLessonFormChange('teacher_id', v)}
                disabled={isTeacher && myTeacherId ? true : false}
              >
                <SelectTrigger data-testid="lesson-teacher">
                  <SelectValue placeholder={language === 'fr' ? 'Sélectionner un enseignant' : 'Select a teacher'} />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((tt) => (
                    <SelectItem key={tt.id} value={tt.id}>
                      {tt.first_name} {tt.last_name}{tt.specialization ? ` — ${tt.specialization}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {language === 'fr'
                  ? 'Par défaut : l\'enseignant du cours. Modifiable si un autre prof anime cette leçon.'
                  : "Defaults to the course teacher. Editable if a different teacher runs this lesson."}
              </p>
            </div>

            {lessonForm.lesson_type === 'video' && (
              <div className="space-y-4">
                <Tabs 
                  value={lessonForm.video_source} 
                  onValueChange={(value) => handleLessonFormChange('video_source', value)}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="youtube" className="flex items-center gap-2">
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </TabsTrigger>
                    <TabsTrigger value="bunny" className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      {language === 'fr' ? 'Téléverser' : 'Upload'}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="youtube" className="space-y-3 mt-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-red-500" />
                        {language === 'fr' ? 'URL YouTube' : 'YouTube URL'} *
                      </Label>
                      <Input
                        value={lessonForm.youtube_url}
                        onChange={(e) => handleLessonFormChange('youtube_url', e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        data-testid="lesson-youtube-url"
                      />
                      {lessonForm.youtube_url && extractYouTubeId(lessonForm.youtube_url) && (
                        <div className="mt-2 h-48 rounded-lg overflow-hidden bg-black">
                          <iframe
                            src={`https://www.youtube.com/embed/${extractYouTubeId(lessonForm.youtube_url)}`}
                            title="Preview"
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          />
                        </div>
                      )}
                      <p className="text-xs text-slate-500">
                        {language === 'fr' 
                          ? 'Formats acceptés: youtube.com/watch?v=... ou youtu.be/...'
                          : 'Accepted formats: youtube.com/watch?v=... or youtu.be/...'
                        }
                      </p>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="bunny" className="space-y-3 mt-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-emerald-600" />
                        {language === 'fr' ? 'Téléverser une vidéo' : 'Upload Video'}
                      </Label>
                      
                      {lessonForm.bunny_video_url ? (
                        <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Video className="w-5 h-5 text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                {language === 'fr' ? 'Vidéo téléversée' : 'Video uploaded'}
                              </span>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleLessonFormChange('bunny_video_url', '')}
                              className="text-red-600 hover:text-red-700"
                            >
                              {language === 'fr' ? 'Supprimer' : 'Remove'}
                            </Button>
                          </div>
                          <p className="text-xs text-emerald-600 mt-2 truncate" title={lessonForm.bunny_video_url}>
                            {(lessonForm.bunny_video_url || '').split('/').pop()}
                          </p>
                          <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-black">
                            <video 
                              src={lessonForm.bunny_video_url} 
                              controls 
                              preload="metadata"
                              className="w-full h-full"
                              onError={() => toast.error(language === 'fr' ? 'Impossible de charger la vidéo — vérifiez le fichier' : 'Unable to load video — check the file')}
                            />
                          </div>
                        </div>
                      ) : (
                        <VideoUploader
                          lessonId={editingLesson?.id}
                          onUploadSuccess={handleVideoUploadSuccess}
                          onUploadError={(err) => toast.error(err)}
                          maxSizeMB={250}
                        />
                      )}
                      
                      <p className="text-xs text-slate-500">
                        {language === 'fr' 
                          ? 'Formats acceptés: MP4, WebM, AVI, MOV, MKV • Maximum: 250 MB'
                          : 'Accepted formats: MP4, WebM, AVI, MOV, MKV • Maximum: 250 MB'
                        }
                      </p>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            )}

            {lessonForm.lesson_type === 'pdf' && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  {language === 'fr' ? 'Document PDF' : 'PDF Document'}
                </Label>

                {lessonForm.pdf_url ? (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-blue-700 truncate">
                          {language === 'fr' ? 'PDF téléversé' : 'PDF uploaded'}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setLessonForm(prev => ({ ...prev, pdf_url: '', pdf_pages: null }))}
                        className="text-red-600 hover:text-red-700"
                        data-testid="lesson-pdf-remove"
                      >
                        {language === 'fr' ? 'Remplacer' : 'Replace'}
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-2 truncate" title={lessonForm.pdf_url}>
                      {(lessonForm.pdf_url || '').split('/').pop()}
                    </p>
                  </div>
                ) : (
                  <PDFUploader
                    language={language}
                    onUploadSuccess={(data) => setLessonForm(prev => ({ ...prev, pdf_url: data.cdn_url, pdf_pages: data.pdf_pages || null }))}
                    onUploadError={(err) => toast.error(err)}
                    maxSizeMB={50}
                  />
                )}

                <p className="text-xs text-slate-500">
                  {language === 'fr'
                    ? 'Le fichier sera stocké de façon sécurisée (Bunny Storage) et seul son lien sera enregistré dans la leçon. Maximum: 50 MB.'
                    : 'The file will be stored securely (Bunny Storage); only its link is saved in the lesson. Max: 50 MB.'}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch
                checked={lessonForm.is_published}
                onCheckedChange={(checked) => handleLessonFormChange('is_published', checked)}
                data-testid="lesson-published"
              />
              <Label>
                {lessonForm.is_published 
                  ? (language === 'fr' ? 'Leçon publiée' : 'Lesson published')
                  : (language === 'fr' ? 'Leçon en brouillon' : 'Lesson is draft')
                }
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSaveLesson}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-lesson-btn"
            >
              {saving ? (
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

